import React, { useMemo } from "react";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Checkbox } from "~/components/ui/checkbox";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import Confetti from "react-confetti";

interface TestQuestionProps {
  question: {
    id: number;
    questionText: string;
    questionType: "multiple_choice" | "multiple_response";
    options: Array<{ id: number; text: string }>;
    correctAnswerIds: number[];
    explanation: string;
  };
  examTitle: string;
  currentQuestionIndex: number;
  totalQuestions: number;
  submittedAnswer?: number | number[];
  isAnswered: boolean;
  isCorrect?: boolean;
  attemptId: number;
  examId: number;
  hasPaid: boolean;
  onNextQuestion: () => void;
  onAnswerSelect: (answerId: number | number[]) => void;
  onSubmitAnswer: () => void;
  showFinishButton?: boolean;
  onFinishExam?: () => void;
  isReviewMode?: boolean;
  onPriorQuestion?: () => void;
}

const TestQuestion = ({
  question,
  examTitle,
  currentQuestionIndex,
  totalQuestions,
  submittedAnswer,
  isAnswered,
  isCorrect,
  onNextQuestion,
  onAnswerSelect,
  onSubmitAnswer,
  showFinishButton = false,
  onFinishExam,
  isReviewMode = false,
  onPriorQuestion,
}: TestQuestionProps) => {
  const isMultipleChoice = question.questionType === "multiple_choice";

  const renderOptions = useMemo(() => {
    const handleMultipleResponseChange = (optionId: number) => {
      const newAnswer = Array.isArray(submittedAnswer)
        ? submittedAnswer.includes(optionId)
          ? submittedAnswer.filter((id) => id !== optionId)
          : [...submittedAnswer, optionId]
        : [optionId];
      onAnswerSelect(newAnswer);
    };

    if (isMultipleChoice) {
      return (
        <RadioGroup
          value={submittedAnswer?.toString()}
          onValueChange={(value) => onAnswerSelect(Number(value))}
          className="mb-4 space-y-1"
          disabled={isAnswered}
        >
          {question.options.map((option) => (
            <div key={option.id} className="flex items-center">
              <RadioGroupItem
                value={option.id.toString()}
                id={`option-${option.id}`}
                disabled={isAnswered}
                className="border-2 shadow-md border-blue-600 text-blue-600"
              />
              <Label
                htmlFor={`option-${option.id}`}
                className="ml-2 text-lg text-gray-900"
              >
                {option.text}
              </Label>
            </div>
          ))}
        </RadioGroup>
      );
    } else {
      return (
        <div className="mb-4 space-y-1">
          {question.options.map((option) => (
            <div key={option.id} className="flex items-center">
              <Checkbox
                id={`option-${option.id}`}
                checked={
                  Array.isArray(submittedAnswer) &&
                  submittedAnswer.includes(option.id)
                }
                onCheckedChange={() => handleMultipleResponseChange(option.id)}
                disabled={isAnswered}
                className="border-2 shadow-md border-blue-600 text-blue-600"
              />
              <Label
                htmlFor={`option-${option.id}`}
                className="ml-2 text-lg text-gray-900"
              >
                {option.text}
              </Label>
            </div>
          ))}
        </div>
      );
    }
  }, [
    isMultipleChoice,
    question.options,
    submittedAnswer,
    isAnswered,
    onAnswerSelect,
  ]);

  return (
    <Card className="w-full max-w-2xl p-6 bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl font-bold mb-4 text-gray-900">{examTitle}</h2>
      <h3 className="text-xl font-bold mb-4 text-gray-900">
        Question {currentQuestionIndex + 1} of {totalQuestions}
      </h3>
      <p className="mb-4 text-lg text-gray-900">{question.questionText}</p>
      {renderOptions}
      {isAnswered && isCorrect === false && (
        <div className="mt-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700">
          <p className="font-bold">
            Incorrect. The correct answer
            {question.correctAnswerIds.length > 1 ? "s are" : " is"}:{" "}
            {question.options
              .filter((o) => question.correctAnswerIds.includes(o.id))
              .map((o) => o.text)
              .join(", ")}
          </p>
          <p>{question.explanation}</p>
        </div>
      )}
      {isCorrect && (
        <div className="mt-4 p-4 bg-green-100 border-l-4 border-green-500 text-green-700">
          <p className="font-bold">Correct!</p>
          <p>{question.explanation}</p>
        </div>
      )}
      <div className="mt-6 flex justify-end space-x-4">
        {isAnswered ? (
          <>
            {isReviewMode && currentQuestionIndex > 0 && (
              <Button
                onClick={onPriorQuestion}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                Prior Question
              </Button>
            )}
            {showFinishButton ? (
              <Button
                onClick={onFinishExam}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {isReviewMode ? "Back to Home" : "Finish Exam"}
              </Button>
            ) : (
              <Button
                onClick={onNextQuestion}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                Next Question
              </Button>
            )}
          </>
        ) : (
          <Button
            onClick={onSubmitAnswer}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            Submit Answer
          </Button>
        )}
      </div>
      {isCorrect && !isReviewMode && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={200}
          gravity={0.7}
          wind={0.02}
        />
      )}
    </Card>
  );
};

export default React.memo(TestQuestion);
